import React, { useState, useCallback, useContext } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { LocaleContext } from "@src/context/locale-context"
import { FormActions } from "@src/components/form"
import Button from "@src/components/core-button"
import { formatNumber } from "@src/utils/numbers"
import Spinner from "@src/components/core-spinner"
import { SITE_DOMAIN } from "@src/utils/constants"
import Input from "../../reusable/forms/input"
import InputArea from "../../reusable/forms/input-area"
import { useFormContext } from "react-hook-form"
import { required } from "@src/utils/validators"
import GTM from "@src/services/gtm"
import NetlifyForms from "@src/services/netlify/forms"

const StyledFormActions = styled(FormActions)`
  & > button {
    margin-bottom: 0px;
  }
  @media only screen and (${props => props.theme.screen.small.min}) {
    align-items: flex-start;
  }
`

const Row = styled.div`
  display: flex;
  gap: 16px;
`

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .hidden {
    display: none;
  }

  @media only screen and (${props => props.theme.screen.small.min}) {
    & > .narrow {
      width: 49%;
    }
    & > .full-width {
      width: 100%;
    }
    & > .left,
    & > ${StyledFormActions} {
      float: left;
    }
    & > .right {
      float: right;
    }
  }
`

const QuestionForm = ({
  name: formName,
  pageName,
  pagePath,
  operator,
  productPrice,
  onClose,
}) => {
  const { convertCurrency } = useContext(LocaleContext)
  const [formStatus, setFormStatus] = useState(null)

  const { handleSubmit } = useFormContext()

  const handleSuccessClick = () => {
    onClose()
    // Reset form so user can ask more questions
    setFormStatus(null)
  }
  const onSubmit = useCallback(() => {
    setFormStatus({
      processing: "Please wait a moment while we save your message...",
    })
  }, [setFormStatus])

  const onSubmitError = useCallback(
    error => {
      setFormStatus({ error: error.message })
    },
    [setFormStatus],
  )
  const onSubmitSuccess = useCallback(
    result => {
      if (result.netlify) {
        setFormStatus({
          success:
            "Thanks for the message! Our team will get back to you soon.",
        })
      } else {
        onSubmitError({
          message: "There was an error saving the message.",
        })
      }
    },
    [setFormStatus, onSubmitError],
  )
  const generateDataLayer = () => {
    const analyticsCurrency = process.env.GATSBY_ANALYTICS_CURRENCY
    const dataPrice = !productPrice
      ? 0
      : parseFloat(
          formatNumber(
            convertCurrency(
              productPrice.amount,
              productPrice.currency.code,
              analyticsCurrency,
            ),
            2,
            "",
          ),
        )
    const dataLayer = {
      guest_count: 0,
      [`price_${analyticsCurrency}`]:
        !dataPrice || isNaN(dataPrice) ? 0 : dataPrice,
      [`total_${analyticsCurrency}`]: 0,
    }
    return dataLayer
  }

  if (pagePath && !pagePath.startsWith(SITE_DOMAIN)) {
    pagePath = `${SITE_DOMAIN}${pagePath}`
  }

  const submit = async data => {
    let name = formName

    try {
      const submitData = data
      const result = {}

      result.submit = onSubmit(submitData)

      result.netlify = await NetlifyForms.submitForm(name, submitData)

      const dataLayer = {
        event: "10a.form.success",
      }
      if (name) {
        dataLayer["form-name"] = name
      }

      const addDataLayer = generateDataLayer(submitData)
      if (addDataLayer) {
        for (const key in addDataLayer) {
          dataLayer[key] = addDataLayer[key]
        }
      }

      GTM.dataLayerPush(dataLayer)
      onSubmitSuccess(result)
    } catch (error) {
      onSubmitError(error)
    }
  }

  return (
    <>
      {formStatus && formStatus.success ? (
        <>
          <p>{formStatus.success}</p>
          <Button onClick={handleSuccessClick}>Close</Button>
        </>
      ) : (
        <StyledForm>
          <Row>
            <Input
              name={`${formName}-first-name`}
              placeholder="First name"
              validate={required("First name is required to subscribe.")}
              className="narrow left"
            />
            <Input
              name={`${formName}-last-name`}
              placeholder="Last name"
              validate={required("Last name is required to subscribe.")}
              className="narrow right"
            />
          </Row>
          <Input
            name={`${formName}-email`}
            placeholder="Email"
            validate={required("Let us know where we can email a reply!")}
            className="full-width left"
          />
          <InputArea
            name={`${formName}-message`}
            placeholder="Message"
            validate={required(
              "We're happy to answer any questions about our products and services",
            )}
            className="full-width left"
          />
          {/* Input below is to pass tour or operator name to netlify */}
          <Input
            name={`${formName}-page-name`}
            defaultValue={pageName}
            className="hidden"
          />
          {operator && (
            <Input
              name={`${formName}-operator-name`}
              defaultValue={operator}
              className="hidden"
            />
          )}
          {pagePath && (
            <Input
              name={`${formName}-cms-link`}
              defaultValue={pagePath.replace(
                SITE_DOMAIN,
                "cms.10adventures.com",
              )}
              className="hidden"
            />
          )}
          {pagePath && (
            <Input
              name={`${formName}-page-url`}
              defaultValue={pagePath}
              className="hidden"
            />
          )}
          <StyledFormActions
            submitting={formStatus && formStatus.processing ? true : undefined}
          >
            {(formStatus?.error && (
              <p className="form-error">{formStatus.error}</p>
            )) ||
              (formStatus?.processing && (
                <p className="form-info">{formStatus.processing}</p>
              ))}
            <Button
              type="submit"
              onClick={() => handleSubmit(submit)()}
              disabled={formStatus && formStatus.processing ? true : undefined}
            >
              {formStatus?.processing ? (
                <Spinner color="white" size="sm" />
              ) : (
                "Send"
              )}
            </Button>
          </StyledFormActions>
        </StyledForm>
      )}
    </>
  )
}
export default QuestionForm

QuestionForm.propTypes = {
  name: PropTypes.string.isRequired,
  pageName: PropTypes.string.isRequired,
  pagePath: PropTypes.string,
  operator: PropTypes.string,
  productPrice: PropTypes.shape({
    amount: PropTypes.number,
    amountBase: PropTypes.number,
    currency: PropTypes.shape({
      code: PropTypes.string,
      symbol: PropTypes.string,
    }),
  }),
  onClose: PropTypes.func.isRequired,
}
